import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background grain">
      <div className="relative hidden lg:block">
        <img
          src="https://images.pexels.com/photos/6615806/pexels-photo-6615806.jpeg"
          alt="Interior"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute bottom-0 left-0 p-12 text-white">
          <p className="text-xs font-semibold tracking-[0.35em] uppercase opacity-80">Ordine3</p>
          <h2 className="font-heading text-4xl font-light mt-3 leading-tight">
            Studio di progettazione<br />e relooking online
          </h2>
          <p className="text-sm mt-4 opacity-70 max-w-sm">
            Il gestionale che accompagna ogni progetto dalla progettazione al cantiere.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 relative z-10">
        <div className="w-full max-w-sm fade-up">
          <p className="text-xs font-semibold tracking-[0.35em] uppercase text-muted-foreground">
            Ordine3 · Gestionale
          </p>
          <h1 className="font-heading text-4xl font-light tracking-tight mt-4">Accedi</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Inserisci le credenziali del tuo account.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-semibold tracking-[0.1em] uppercase text-muted-foreground">
                Email
              </Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@dominio.it"
                className="rounded-none border-border focus-visible:ring-foreground h-11"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold tracking-[0.1em] uppercase text-muted-foreground">
                Password
              </Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-none border-border focus-visible:ring-foreground h-11"
                required
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-destructive">
                {error}
              </p>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full rounded-none h-11 bg-foreground text-background hover:bg-foreground/90 active:scale-[0.99] transition-transform"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entra"}
            </Button>
          </form>

          <p className="text-xs text-muted-foreground mt-10">
            Accesso riservato ai collaboratori del progetto. Le credenziali sono fornite dallo studio Ordine3.
          </p>
        </div>
      </div>
    </div>
  );
}
